import React, {
  useState, useEffect, useCallback, useRef,
} from 'react'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'

// Tunable animation parameters — adjust these constants to tweak the feel
export const PICK_A_CARD_NUM_CARDS_DESKTOP = 16
// Mobile fans fewer cards so tap targets aren't overlapping — at <768px the
// 16-card fan packs tightly enough that aiming for a specific card is hard.
export const PICK_A_CARD_NUM_CARDS_MOBILE = 12
export const PICK_A_CARD_MOBILE_BREAKPOINT_PX = 768
export const PICK_A_CARD_THETA_DEG = 24
export const PICK_A_CARD_T_FLAY_MS = 200
export const PICK_A_CARD_OFFSET_PX = 100
export const PICK_A_CARD_T_PICK_MS = 150
export const PICK_A_CARD_T_HANG_MS = 300
export const PICK_A_CARD_T_FLY_MS = 600
export const PICK_A_CARD_HOVER_MS = 100
// Minimum gap from the rotation-widened bounding box of the outermost card to
// the viewport edge — caps the arc radius so cards never clip the screen.
export const PICK_A_CARD_EDGE_MARGIN_PX = 40
// Vertical gap between the "Draw your card" label and the top edge of a
// hovered card.
export const PICK_A_CARD_LABEL_GAP_PX = 48
// Pre-measurement fallback used on the first render before window.innerWidth
// is read into state — keeps geometry sane during the single render where
// viewportWidth is still 0.
export const PICK_A_CARD_FALLBACK_VIEWPORT_PX = 320

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

interface TarotPickACardProps {
  backImageUrl: string
  jiggleTarget: Rect
  onComplete: () => void
}

type InternalPhase = 'waiting' | 'picking' | 'hanging' | 'flying'

export const TarotPickACard: React.FC<TarotPickACardProps> = ({
  backImageUrl,
  jiggleTarget,
  onComplete,
}) => {
  const drawYourCard = useTranslatedText({ keys: ['tarotPickACard', 'drawYourCard'] })
  const cardAriaLabelTemplate = useTranslatedText({ keys: ['tarotPickACard', 'cardAriaLabel'] })

  const [fanned, setFanned] = useState(false)
  const [flayDone, setFlayDone] = useState(false)
  const [internalPhase, setInternalPhase] = useState<InternalPhase>('waiting')
  const [pickedIndex, setPickedIndex] = useState<number | null>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [viewportWidth, setViewportWidth] = useState(0)
  const [imageDecoded, setImageDecoded] = useState(false)
  // Hold onComplete in a ref so phase effects don't restart when the parent
  // passes a new function reference.
  const onCompleteRef = useRef(onComplete)
  useEffect(() => { onCompleteRef.current = onComplete }, [onComplete])
  const thetaRad = (PICK_A_CARD_THETA_DEG * Math.PI) / 180

  const cardWidth = jiggleTarget.width
  const cardHeight = jiggleTarget.height

  useEffect(() => {
    setViewportWidth(window.innerWidth)
  }, [])

  // Fan origin: viewport center horizontally (so the fan is symmetrical and
  // stays on-screen even when the jiggleTarget is off-center because it's
  // centered in the chat content area, not the viewport). Vertically anchored
  // at jiggleTarget bottom so a card at translate(landingDx, 0) rotate(0)
  // lands exactly on the jiggleTarget rect.
  const fanOriginX = viewportWidth > 0
    ? viewportWidth / 2
    : PICK_A_CARD_FALLBACK_VIEWPORT_PX / 2
  const fanOriginY = jiggleTarget.top + jiggleTarget.height

  // Choose the card count based on the measured viewport. Before measurement
  // (viewportWidth === 0) we default to the mobile count, matching the
  // PICK_A_CARD_FALLBACK_VIEWPORT_PX mobile-sized geometry fallback.
  const numCards = viewportWidth >= PICK_A_CARD_MOBILE_BREAKPOINT_PX
    ? PICK_A_CARD_NUM_CARDS_DESKTOP
    : PICK_A_CARD_NUM_CARDS_MOBILE

  // Horizontal offset from fan origin to jiggleTarget center — the picked
  // card translates to this on entry to 'flying' so it lands at jiggleTarget.
  const landingDx = (jiggleTarget.left + jiggleTarget.width / 2) - fanOriginX

  // Arc spans up to ~80% of viewport width, but capped so the outermost card —
  // including the rotation-widened bounding box — stays at least
  // PICK_A_CARD_EDGE_MARGIN_PX inside the viewport on each side. A card of W×H
  // rotated by alpha around its bottom-center extends
  // |W/2·cos(alpha)| + |H·sin(alpha)| from rotation center.
  const effectiveWidth = viewportWidth > 0 ? viewportWidth : PICK_A_CARD_FALLBACK_VIEWPORT_PX
  const sinHalfTheta = Math.sin(thetaRad / 2)
  const cosHalfTheta = Math.cos(thetaRad / 2)
  const cardBboxHalfExtent = (cardWidth / 2) * cosHalfTheta + cardHeight * sinHalfTheta
  const idealRadius = (effectiveWidth * 0.4) / sinHalfTheta
  const maxRadius = Math.max(
    0,
    (effectiveWidth / 2 - cardBboxHalfExtent - PICK_A_CARD_EDGE_MARGIN_PX) / sinHalfTheta,
  )
  const radius = Math.min(idealRadius, maxRadius)

  const getCardAngle = useCallback((i: number): number => {
    const t = numCards > 1 ? i / (numCards - 1) : 0.5
    return (t - 0.5) * thetaRad
  }, [thetaRad, numCards])

  // Pre-decode the card back image for THIS render context. The upstream
  // cardBackImageReady gate (in OracleExistingChat) only guarantees bytes are
  // downloaded — onLoad fires when bytes arrive but not necessarily when the
  // pixels are ready to paint, and img.decode() on a separately created
  // Image() instance doesn't reliably warm the decoded-pixel cache for the
  // fresh <img> tags this component renders. So we re-decode here to
  // guarantee they paint synchronously instead of flashing slivers.
  useEffect(() => {
    let cancelled = false
    const img = new Image()
    img.src = backImageUrl
    img.decode().then(() => {
      if (!cancelled) setImageDecoded(true)
    }).catch(() => {
      // Don't block the fan-out forever if decode rejects (e.g. CORS/abort)
      if (!cancelled) setImageDecoded(true)
    })
    return () => { cancelled = true }
  }, [backImageUrl])

  // Trigger fan-out only once viewport is measured AND image is fully decoded
  useEffect(() => {
    if (viewportWidth === 0 || !imageDecoded) return undefined
    const raf = requestAnimationFrame(() => setFanned(true))
    return () => cancelAnimationFrame(raf)
  }, [viewportWidth, imageDecoded])

  useEffect(() => {
    if (!fanned) return undefined
    const timer = global.setTimeout(() => setFlayDone(true), PICK_A_CARD_T_FLAY_MS)
    return () => global.clearTimeout(timer)
  }, [fanned])

  // picking → hanging
  useEffect(() => {
    if (internalPhase !== 'picking') return undefined
    const timer = global.setTimeout(() => setInternalPhase('hanging'), PICK_A_CARD_T_PICK_MS)
    return () => global.clearTimeout(timer)
  }, [internalPhase])

  // hanging → flying
  useEffect(() => {
    if (internalPhase !== 'hanging') return undefined
    const timer = global.setTimeout(() => setInternalPhase('flying'), PICK_A_CARD_T_HANG_MS)
    return () => global.clearTimeout(timer)
  }, [internalPhase])

  // flying → onComplete. The CSS transition handles the visual motion; this timer
  // matches its duration and tells the parent to take over (jiggle phase).
  useEffect(() => {
    if (internalPhase !== 'flying') return undefined
    const timer = global.setTimeout(() => onCompleteRef.current(), PICK_A_CARD_T_FLY_MS)
    return () => global.clearTimeout(timer)
  }, [internalPhase])

  const handleCardClick = useCallback((i: number) => {
    if (internalPhase !== 'waiting') return
    setHoveredIndex(null)
    setPickedIndex(i)
    setInternalPhase('picking')
  }, [internalPhase])

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    left: fanOriginX,
    top: fanOriginY,
    width: 0,
    height: 0,
    zIndex: 1000,
  }

  // Label sits PICK_A_CARD_LABEL_GAP_PX above the top edge of a hovered card.
  // Hover lifts a card by PICK_A_CARD_OFFSET_PX / 2 along its normal, so we
  // add that to the gap. The label stays in the DOM throughout the pick
  // sequence so opacity can animate to 0 — conditional rendering would snap.
  const labelStyle: React.CSSProperties = {
    position: 'fixed',
    left: 0,
    top: fanOriginY - cardHeight - PICK_A_CARD_OFFSET_PX / 2 - PICK_A_CARD_LABEL_GAP_PX,
    width: '100%',
    textAlign: 'center',
    pointerEvents: 'none',
    zIndex: 1001,
    opacity: pickedIndex === null ? 1 : 0,
    transition: `opacity ${PICK_A_CARD_T_PICK_MS}ms ease-out`,
  }

  return (
    <>
      <div className="tarot-pick-a-card__backdrop" aria-hidden="true" />
      <p className="tarot-pick-a-card__label" style={labelStyle}>
        {drawYourCard}
      </p>
      <div
        className="tarot-pick-a-card__container"
        style={containerStyle}
        role="group"
        aria-label={drawYourCard}
      >
        {Array.from({ length: numCards }, (_, i) => {
          const alpha = getCardAngle(i)
          const isPicked = i === pickedIndex
          const isOther = pickedIndex !== null && !isPicked
          const isHovered = internalPhase === 'waiting' && hoveredIndex === i
          const hoverOffset = isHovered ? PICK_A_CARD_OFFSET_PX / 2 : 0

          let dx: number
          let dy: number
          let rotation: number

          if (!fanned) {
            // Stacked at fan origin (viewport center) before the flay-out
            dx = 0; dy = 0; rotation = 0
          } else if (isPicked && internalPhase === 'flying') {
            // Fly to the jiggleTarget rect (offset from viewport-center fan origin)
            dx = landingDx; dy = 0; rotation = 0
          } else if (isPicked && (internalPhase === 'picking' || internalPhase === 'hanging')) {
            // Lifted along the card's surface normal
            dx = (radius + PICK_A_CARD_OFFSET_PX) * Math.sin(alpha)
            dy = radius * (1 - Math.cos(alpha)) - PICK_A_CARD_OFFSET_PX * Math.cos(alpha)
            rotation = alpha * (180 / Math.PI)
          } else {
            // Normal fan position (with optional outward hover nudge)
            dx = radius * Math.sin(alpha) + hoverOffset * Math.sin(alpha)
            dy = radius * (1 - Math.cos(alpha)) - hoverOffset * Math.cos(alpha)
            rotation = alpha * (180 / Math.PI)
          }

          const transition = (() => {
            if (viewportWidth === 0) return 'none'
            if (internalPhase === 'waiting') {
              const ms = flayDone ? PICK_A_CARD_HOVER_MS : PICK_A_CARD_T_FLAY_MS
              return `transform ${ms}ms ease-out`
            }
            if (internalPhase === 'picking') {
              return `transform ${PICK_A_CARD_T_PICK_MS}ms ease-out, opacity ${PICK_A_CARD_T_PICK_MS}ms ease-out`
            }
            // 'hanging' and 'flying' share this transition string. The fly
            // transform is applied on entry to 'flying'; declaring the same
            // transition one phase earlier (in 'hanging') pre-arms it so the
            // browser animates the transform change instead of snapping —
            // a transition declared on the same frame as a transform change
            // doesn't take effect.
            return `transform ${PICK_A_CARD_T_FLY_MS}ms ease-in-out`
          })()

          const cardStyle: React.CSSProperties = {
            position: 'absolute',
            left: -cardWidth / 2,
            top: -cardHeight,
            width: cardWidth,
            height: cardHeight,
            transformOrigin: 'bottom center',
            transform: `translateX(${dx}px) translateY(${dy}px) rotate(${rotation}deg)`,
            transition,
            opacity: isOther ? 0 : 1,
            zIndex: isPicked ? numCards : i,
            cursor: internalPhase === 'waiting' ? 'pointer' : 'default',
          }

          // useTranslatedText returns the template string un-substituted
          // because we can't call the hook 16 times — substitute per-card.
          const ariaLabel = cardAriaLabelTemplate?.replace('{number}', String(i + 1))
            ?? `Card ${i + 1}`

          return (
            <button
              type="button"
              key={i}
              className="tarot-pick-a-card__card"
              style={cardStyle}
              onClick={() => handleCardClick(i)}
              onMouseEnter={() => { if (internalPhase === 'waiting') setHoveredIndex(i) }}
              onMouseLeave={() => setHoveredIndex(null)}
              disabled={internalPhase !== 'waiting'}
              aria-label={ariaLabel}
            >
              <img
                src={backImageUrl}
                alt=""
                draggable={false}
                decoding="sync"
                className="tarot-pick-a-card__card-img"
              />
            </button>
          )
        })}
      </div>
    </>
  )
}
